import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error", "inputRef", "placeholder", "styled", "type", "use", "disabled"];
import { forwardRef, useContext, memo } from 'react';
import { lazyEval } from '../../utils/lazyEval';
import ShareInput from '../../components/ShareInput/ShareInput';
import styled from 'foundations-theming/utils';
import { rgba } from '../../utils/color';
import { FieldsetContext } from '../../context/FieldsetContext';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useMicroDateInputContext } from '../MicroDateInput/MicroDateInputContext';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { useMicroTimeInputContext } from '../MicroTimeInput/MicroTimeInputContext';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import usePlaceholder from '../../../hooks/usePlaceholder';
import { MERCURY_LAYER } from '../../constants/Layers';
import { jsx as _jsx } from "react/jsx-runtime";
const TYPES = ['text', 'email', 'number', 'password', 'search', 'url', 'tel'];
export const StyledInput = styled.input.withConfig({
  displayName: "TextInput__StyledInput",
  componentId: "sc-1ekef6a-0"
})(({
  $small,
  $styled,
  $error,
  theme,
  $cssStyles,
  $use,
  $styleOverrides,
  $isDisplayOptimized
}) => {
  const $hasMinimalStyling = $isDisplayOptimized || !$styled;
  const readOnlyAndDisabledStyles = {
    backgroundColor: theme.color['fill-field-disabled'],
    borderColor: theme.color['border-core-disabled'],
    boxShadow: 'none'
  };
  return deepMerge(Object.assign({
    backgroundColor: $use === 'on-dark' ? theme.color['fill-surface-default'] : theme.color['fill-field-default'],
    border: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
    borderRadius: theme.borderRadius['100'],
    color: theme.color['text-core-default'],
    display: 'block',
    fontFamily: $use === 'code' ? theme.typography['code-100'].fontFamily : 'inherit',
    fontSize: $small ? theme.typography['body-100'].fontSize : theme.typography['body-200'].fontSize,
    fontWeight: $use === 'code' ? theme.typography['code-100'].fontWeight : 'inherit',
    height: $isDisplayOptimized ? '30px' : $small ? '32px' : '40px',
    lineHeight: theme.typography['body-200'].lineHeight,
    letterSpacing: theme.typography['body-200'].letterSpacing,
    paddingInline: $isDisplayOptimized ? theme.space['0'] : theme.space['200'],
    paddingBlock: $small || $isDisplayOptimized ? theme.space['100'] : theme.space['200'],
    textAlign: 'left',
    transition: 'all 150ms ease-out',
    verticalAlign: 'middle',
    width: '100%',
    // Focus styles
    '&:focus': {
      borderColor: theme.color['border-interactive-default'],
      boxShadow: `0 0 4px 1px ${rgba(`${theme.color['border-interactive-default']}`, 0.8)}, 0 0 0 1px ${theme.color['border-interactive-default']}`,
      outline: 0
    },
    // Placeholder plus disabled styles
    '&:disabled::placeholder': {
      color: theme.color['text-core-disabled']
    },
    // Placeholder styles
    '&::placeholder': {
      color: theme.color['text-core-subtle']
    },
    '&[readonly]': {
      "&:not([aria-expanded='true'])": Object.assign({}, readOnlyAndDisabledStyles, {
        color: theme.color['text-core-subtle']
      })
    }
  }, $hasMinimalStyling && {
    backgroundColor: 'transparent !important',
    borderColor: 'transparent !important',
    boxShadow: 'none !important'
  }, $isDisplayOptimized && {
    border: 'none',
    fontSize: 'var(--trellis-typography-body-100-font-size)',
    paddingInlineStart: '0'
  }, $error && !$hasMinimalStyling && {
    zIndex: MERCURY_LAYER,
    borderColor: `${theme.color['border-alert-default']} !important`,
    backgroundColor: theme.color['fill-field-default'],
    boxShadow: `0 0 0 1px ${theme.color['border-alert-default']} !important`,
    '&:focus': {
      boxShadow: `0 0 2px 2px ${theme.color['border-alert-default']} !important`,
      outline: 0
    }
  }, {
    // Disabled styles
    '&:disabled': Object.assign({}, readOnlyAndDisabledStyles, {
      color: theme.color['text-core-disabled'],
      cursor: 'not-allowed'
    })
  }, $cssStyles), $styleOverrides);
});
export const TextInputWithRef = (_ref, ref) => {
  var _microDateInputContex, _microTimeInputContex;
  let {
      error = false,
      inputRef,
      placeholder,
      styled: _styled = true,
      type = 'text',
      use = 'default',
      disabled
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps,
    callbackProps,
    restProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = useContext(FieldsetContext);
  const microDateInputContext = useMicroDateInputContext();
  const microTimeInputContext = useMicroTimeInputContext();
  const isDisplayOptimized = useIsDisplayOptimized();
  const computedDisabled = disabled || fieldDisabled;
  const computedPlaceholder = usePlaceholder(lazyEval(placeholder));
  return /*#__PURE__*/_jsx(StyledInput, Object.assign({}, domProps, callbackProps, restProps, {
    $cssStyles: cssStyles,
    $error: error,
    $isDisplayOptimized: isDisplayOptimized,
    $small: fieldSize === 'small',
    $styled: _styled,
    $styleOverrides: deepMerge((_microDateInputContex = microDateInputContext === null || microDateInputContext === void 0 ? void 0 : microDateInputContext.microInputStyleOverrides) !== null && _microDateInputContex !== void 0 ? _microDateInputContex : {}, (_microTimeInputContex = microTimeInputContext === null || microTimeInputContext === void 0 ? void 0 : microTimeInputContext.microInputStyleOverrides) !== null && _microTimeInputContex !== void 0 ? _microTimeInputContex : {}),
    $use: use,
    placeholder: computedPlaceholder,
    ref: inputRef || ref,
    type: type,
    onKeyDown: rest.onKeyDown,
    disabled: computedDisabled
  }));
};
export const TextInput = ShareInput( /*#__PURE__*/memo( /*#__PURE__*/forwardRef(TextInputWithRef)));